import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Plus, Users, Calendar, TrendingUp, MessageCircle, Baby, Sparkles, BookOpen, BarChart3, Brain, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getCategoryStats } from '../services/memoryCategoriesService';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryStats, setCategoryStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showAddChild, setShowAddChild] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [childForm, setChildForm] = useState({
    name: '',
    birth_date: '',
    gender: ''
  });
  const [activityForm, setActivityForm] = useState({
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchChildren();
    fetchCategoryStats();
  }, []);

  const fetchChildren = async () => {
    try {
      const response = await axios.get(`${API_BASE}/children`);
      setChildren(response.data);
      
      // Fetch recent activities for all children
      const activitiesPromises = response.data.map(child => 
        axios.get(`${API_BASE}/activities/${child.id}`)
      );
      
      const activitiesResponses = await Promise.all(activitiesPromises);
      const allActivities = activitiesResponses
        .flatMap(response => response.data)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5);
      
      setRecentActivities(allActivities);
    } catch (error) {
      toast.error('Failed to fetch children data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryStats = async () => {
    setLoadingStats(true);
    try {
      const stats = await getCategoryStats();
      setCategoryStats(stats);
    } catch (error) {
      console.error('Failed to fetch category stats:', error);
      // Don't show error toast - this is optional data
    } finally {
      setLoadingStats(false);
    }
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/children`, childForm);
      toast.success('Child added successfully!');
      setShowAddChild(false);
      setChildForm({ name: '', birth_date: '', gender: '' });
      fetchChildren();
    } catch (error) {
      toast.error('Failed to add child');
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!selectedChildId) {
      toast.error('Please select a child');
      return;
    }
    if (!activityForm.description.trim()) {
      toast.error('Please enter an activity description');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/activities`, {
        child_id: selectedChildId,
        description: activityForm.description,
        date: new Date(activityForm.date).toISOString()
      });
      
      toast.success('Activity added successfully! AI analysis in progress...');
      setShowAddActivity(false);
      setActivityForm({ description: '', date: new Date().toISOString().split('T')[0] });
      setSelectedChildId('');
      
      // Refresh activities
      setTimeout(() => {
        fetchChildren();
      }, 1000);
    } catch (error) {
      toast.error('Failed to add activity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAddActivity = () => {
    // Auto-select first child if only one child
    if (children.length === 1) {
      setSelectedChildId(children[0].id);
    }
    setShowAddActivity(true);
  };

  const calculateAge = (birthDate) => {
    const birth = new Date(birthDate);
    const today = new Date();
    const ageInMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    
    if (ageInMonths < 12) {
      return `${ageInMonths} months`;
    } else {
      const years = Math.floor(ageInMonths / 12);
      const months = ageInMonths % 12;
      return months > 0 ? `${years}y ${months}m` : `${years} years`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold font-display mb-4">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </span>{' '}
            <span>👋</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Track your child's amazing journey with AI-powered insights and expert guidance
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="card-hover glass border-0">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{children.length}</p>
                  <p className="text-slate-600">Children</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-hover glass border-0">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{recentActivities.length}</p>
                  <p className="text-slate-600">Recent Activities</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="card-hover glass border-0">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">AI</p>
                  <p className="text-slate-600">Powered Insights</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Children Section */}
          <Card className="glass border-0">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-display text-2xl text-slate-800">Your Children</CardTitle>
                <CardDescription>Manage and view your children's profiles</CardDescription>
              </div>
              <Dialog open={showAddChild} onOpenChange={setShowAddChild}>
                <DialogTrigger asChild>
                  <Button className="btn-hover gradient-emerald text-white" data-testid="add-child-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Child</DialogTitle>
                    <DialogDescription>
                      Add your child's information to start tracking their development.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddChild} className="space-y-4" data-testid="add-child-form">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={childForm.name}
                        onChange={(e) => setChildForm({...childForm, name: e.target.value})}
                        required
                        data-testid="child-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="birth_date">Birth Date</Label>
                      <Input
                        id="birth_date"
                        type="date"
                        value={childForm.birth_date}
                        onChange={(e) => setChildForm({...childForm, birth_date: e.target.value})}
                        required
                        data-testid="child-birth-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gender">Gender</Label>
                      <Select
                        value={childForm.gender}
                        onValueChange={(value) => setChildForm({...childForm, gender: value})}
                        data-testid="child-gender-select"
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full gradient-emerald text-white" data-testid="save-child-button">
                      Add Child
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {children.length === 0 ? (
                <div className="text-center py-8">
                  <Baby className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No children added yet</p>
                  <Button 
                    onClick={() => setShowAddChild(true)}
                    className="btn-hover gradient-emerald text-white"
                  >
                    Add Your First Child
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {children.map((child) => (
                    <div key={child.id} className="flex items-center justify-between p-4 bg-white/50 rounded-lg border border-emerald-200/50 hover:bg-white/70 transition-colors" data-testid={`child-card-${child.name}`}>
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center">
                          <Baby className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{child.name}</h3>
                          <p className="text-sm text-slate-600">{calculateAge(child.birth_date)} • {child.gender}</p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Link to={`/activities/${child.id}`}>
                          <Button variant="outline" size="sm" className="btn-hover" data-testid={`view-activities-${child.name}`}>
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>Activities</span>
                          </Button>
                        </Link>
                        <Link to={`/reports/${child.id}`}>
                          <Button variant="outline" size="sm" className="btn-hover" data-testid={`view-reports-${child.name}`}>
                            <BarChart3 className="w-4 h-4 mr-2" />
                            <span>Reports</span>
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <Card className="glass border-0">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-display text-2xl text-slate-800">Recent Activities</CardTitle>
                <CardDescription>Latest milestones and developments</CardDescription>
              </div>
              {children.length > 0 && (
                <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
                  <DialogTrigger asChild>
                    <Button 
                      className="btn-hover gradient-emerald text-white" 
                      onClick={handleOpenAddActivity}
                      data-testid="add-activity-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Activity
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add New Activity</DialogTitle>
                      <DialogDescription>
                        Record a new milestone or activity for your child.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddActivity} className="space-y-4" data-testid="add-activity-form">
                      {children.length > 1 && (
                        <div>
                          <Label htmlFor="child">Select Child</Label>
                          <Select
                            value={selectedChildId}
                            onValueChange={setSelectedChildId}
                            data-testid="activity-child-select"
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a child" />
                            </SelectTrigger>
                            <SelectContent>
                              {children.map((child) => (
                                <SelectItem key={child.id} value={child.id}>
                                  {child.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div>
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          type="date"
                          value={activityForm.date}
                          onChange={(e) => setActivityForm({...activityForm, date: e.target.value})}
                          required
                          data-testid="activity-date-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="description">Activity Description</Label>
                        <Textarea
                          id="description"
                          value={activityForm.description}
                          onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                          placeholder="Describe what your child did or achieved..."
                          required
                          rows={4}
                          data-testid="activity-description-input"
                        />
                      </div>
                      <Button 
                        type="submit" 
                        className="w-full gradient-emerald text-white"
                        disabled={submitting}
                        data-testid="save-activity-button"
                      >
                        {submitting ? 'Adding...' : 'Add Activity'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </CardHeader>
            <CardContent>
              {recentActivities.length === 0 ? (
                <div className="text-center py-8">
                  <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No activities recorded yet</p>
                  {children.length > 0 && (
                    <Button 
                      onClick={handleOpenAddActivity}
                      className="btn-hover gradient-emerald text-white"
                    >
                      Add Your First Activity
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4" data-testid="recent-activities-list">
                  {recentActivities.map((activity) => {
                    const child = children.find(c => c.id === activity.child_id);
                    return (
                      <div key={activity.id} className="p-3 bg-white/50 rounded-lg border border-emerald-200/50">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-slate-800">{activity.description}</h4>
                          <span className="text-xs text-slate-500">
                            {new Date(activity.date).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-1">{child?.name}</p>
                        {activity.milestone_category && (
                          <span className="milestone-badge">
                            {activity.milestone_category}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {children.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-emerald-200/50">
                      <Link to={`/activities/${children[0].id}`}>
                        <Button variant="outline" className="btn-hover w-full">
                          <Calendar className="w-4 h-4 mr-2" />
                          View All Activities
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
